import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import UserService from "rc-app/services/UserService";
import FilterSelect from "rc-app/components/Filter Select";
import SearchBar from "rc-app/components/Search Bar";
import PaginationRounded from "rc-app/components/Pagination";
import { useNavigate, useLocation } from "react-router-dom";
import { PropertyCard } from "rc-app/components/Card/propertyCard";
import { CircularProgress } from "@mui/material";
import UserContext from "rc-app/context/UserContext";
import AlertDialog from "rc-app/components/AlertDialog";
import Button from "rc-app/components/Button";
import { getAgencyLogo } from "rc-app/api/agencyActionsApi";
import { determineAgencyType, validateAndCleanSearchInput } from "rc-app/utils";
import { getRegionIds } from "rc-app/api/regionActionsApi";
import Icon from "rc-app/components/Icons";
import {
  getCustomerById,
  shortlistPropertiesOfCustomerByEmailId,
} from "rc-app/api/customerActionsApi";
import { getAgentProfile } from "rc-app/api/agentActionsApi";

export const AddShortlist = () => {
  const { isUserExist, setIsUserExist } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { c_email } = location.state || {};
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get("customerId");

  const [customerEmailId, setCustomerEmailId] = useState(c_email || "");
  const [allProperties, setAllProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageLimit = 10;

  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchHint, setSearchHint] = useState("");

  const [shortlistedProperties, setShortlistedProperties] = useState([]);
  const [nonShortlistedProperties, setNonShortlistedProperties] = useState([]);
  const [sellerAgencyId, setSellerAgencyId] = useState("");

  // Loading states with messages
  const [isPropertiesLoading, setIsPropertiesLoading] = useState(false);
  const [isShortlistLoading, setIsShortlistLoading] = useState(false);
  const [isFilteringComplete, setIsFilteringComplete] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Initializing...");

  const [selectedOption, setSelectedOption] = useState({
    agency: "All",
    lifestyle: "All",
    type: "All",
    contract_type: "All",
  });

  const lifestyle: { [key: string]: string } = {
    History: "History",
    Beach: "Beach",
    Waterfront: "Waterfront",
    Lakefront: "Lakefront",
    Metropolitan: "Metropolitan",
    "Country Living": "Country Living",
    "Wine And Vineyards": "Wine And Vineyards",
    "Eco Friendly": "Eco Friendly",
    Mountain: "Mountain",
    Layouts: "Layouts",
    Investments: "Investments",
    Others: "Others",
  };

  const type: { [key: string]: string } = {
    Apartment: "Apartment",
    Castle: "Castle",
    Estate: "Estate",
    "Free Standing House": "Free Standing House",
    Office: "Office",
    Farmhouse: "Farmhouse",
    Palace: "Palace",
    Penthouse: "Penthouse",
    Shop: "Shop",
    Studio: "Studio",
    Villa: "Villa",
    commercial: "Commercial",
    Layout: "Layout",
    Farmland: "Farmland",
    "Farm house": "Farm house",
    Warehouse: "Warehouse",
    "Office space": "Office space",
    "Meta space": "Meta space",
    Others: "Others",
  };

  const contract_type: { [key: string]: string } = {
    Sale: "Sale",
    Rent: "Rent",
    Lease: "Lease",
    Showcase: "Showcase",
    Investment: "Investment",
    Other: "Other",
  };

  // Get customer email
  useEffect(() => {
    if (customerId) {
      getCustomerById(customerId).then((res) => {
        setCustomerEmailId(res.customer?.email_id);
      });
    }
  }, [customerId]);

  // Get agent profile and agency ID
  useEffect(() => {
    getAgentProfile(UserService.getUseremail()).then((res) => {
      setSellerAgencyId(res?.agent?.agency?.id);
    });
  }, []);

  const getAgencyDetails = () => {
    try {
      const details = localStorage.getItem("agencyDetails");
      return details ? JSON.parse(details) : null;
    } catch (error) {
      console.error("Error reading agency details from localStorage:", error);
      return null;
    }
  };

  // Fetch ALL properties at once
  async function fetchAllProperties() {
    try {
      setIsPropertiesLoading(true);
      setLoadingMessage("Loading available properties...");

      const responseAgencyLogo = await getAgencyLogo(
        UserService.getUseremail()
      );
      const agencyTypeDetails = determineAgencyType({
        regions: responseAgencyLogo.region,
      });

      if (agencyTypeDetails.regions[0] === "*") {
        localStorage.setItem(
          "agencyDetails",
          JSON.stringify({
            ...agencyTypeDetails,
            regionsId: agencyTypeDetails.regions,
          })
        );
      } else if (agencyTypeDetails.regions[0] !== "*") {
        const regionsId = await getRegionIds(agencyTypeDetails?.regions);
        localStorage.setItem(
          "agencyDetails",
          JSON.stringify({ ...agencyTypeDetails, regionsId: regionsId })
        );
      }

      const agencyDetails = getAgencyDetails();
      if (!agencyDetails) {
        console.error("No agency details found in localStorage");
        setIsPropertiesLoading(false);
        return;
      }

      const { agencyType, regionsId } = agencyDetails;
      const regionsIdParam = new URLSearchParams();

      if (regionsId && Array.isArray(regionsId)) {
        regionsId.forEach((regionId: any) => {
          regionsIdParam.append("region_ids", regionId);
        });
      }

      const regionsIdString = regionsIdParam.toString();
      const API_BASE_URL = process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL;

      if (!API_BASE_URL) {
        console.error("Missing environment variable");
        setIsPropertiesLoading(false);
        return;
      }

      let API_URL;
      const LARGE_LIMIT = 1000;

      // Build filter parameters
      const filterParams = [
        selectedOption.contract_type !== "All"
          ? `&contract_type=${selectedOption.contract_type}`
          : "",
        selectedOption.lifestyle !== "All"
          ? `&lifestyle=${selectedOption.lifestyle}`
          : "",
        selectedOption.type !== "All"
          ? `&type_of_property=${selectedOption.type}`
          : "",
        searchTerm.trim() !== "" ? `&q=${searchTerm}` : "",
      ]
        .filter((param) => param !== "")
        .join("");

      if (agencyType === "builder") {
        API_URL = `${API_BASE_URL}/api/v1/realestate/views/mp_digital_twin/agent/properties/${UserService.getUseremail()}?sort_by=created_at&sort_order=-1&page=0&limit=${LARGE_LIMIT}${filterParams}`;
      } else if (agencyType === "marketing") {
        API_URL = `${API_BASE_URL}/api/v1/realestate/views/mp_digital_twin/network_properties?sort_by=created_at&sort_order=-1&page=0&limit=${LARGE_LIMIT}&${regionsIdString}&property_visibility=network${filterParams}`;

        if (searchTerm.trim() !== "") {
          API_URL += `&search_fields=address.city,address.province,address.country,name,title,type_of_property,contract_type,property_id`;
        }
      } else {
        console.error("Unknown agency type:", agencyType);
        setIsPropertiesLoading(false);
        return;
      }

      console.log("Fetching ALL properties from:", API_URL);

      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.mp_digital_twin_list) {
        const allFetchedProperties = response.data.mp_digital_twin_list;
        setAllProperties(allFetchedProperties);
        setIsUserExist(true);
        console.log("Loaded ALL properties:", allFetchedProperties.length);
      }

      setIsPropertiesLoading(false);
    } catch (error: any) {
      console.error("Error fetching properties:", error);
      setLoadingMessage("Failed to load properties. Please try again.");

      if (error.name !== "AbortError" && !axios.isCancel(error)) {
        setIsUserExist(false);
      }

      setIsPropertiesLoading(false);
    }
  }

  // Load shortlisted properties
  const loadShortlistedProperties = async () => {
    if (!customerEmailId) return;

    try {
      setIsShortlistLoading(true);
      setLoadingMessage("Loading customer's shortlisted properties...");
      const res = await shortlistPropertiesOfCustomerByEmailId(customerEmailId);
      const shortlisted = res.map((item: any) => item.mp_digital_twin);
      setShortlistedProperties(shortlisted);
      console.log("Loaded shortlisted properties:", shortlisted.length);
    } catch (err) {
      console.error("Error loading shortlisted properties:", err);
      setLoadingMessage(
        "Failed to load shortlisted properties. Please try again."
      );
    } finally {
      setIsShortlistLoading(false);
    }
  };

  // Filter non-shortlisted properties ONLY when both datasets are loaded
  const filterNonShortlistedProperties = () => {
    setLoadingMessage("Filtering available properties...");

    if (allProperties.length === 0 || shortlistedProperties.length === 0) {
      // If either dataset is empty, we still need to handle the filtering
      if (allProperties.length > 0 && !isShortlistLoading) {
        // If we have properties but no shortlisted properties, all properties are non-shortlisted
        setNonShortlistedProperties(allProperties);
        setIsFilteringComplete(true);
        setLoadingMessage("");
      }
      return;
    }

    console.log("Filtering non-shortlisted properties...");
    console.log("All properties count:", allProperties.length);
    console.log("Shortlisted properties count:", shortlistedProperties.length);

    // Create a Set of shortlisted property IDs for faster lookup
    const shortlistedIds = new Set(
      shortlistedProperties.map((property: any) => property.id)
    );

    // Filter out shortlisted properties
    const nonShortlisted = allProperties.filter(
      (property: any) => !shortlistedIds.has(property._id)
    );

    console.log("Non-shortlisted properties count:", nonShortlisted.length);
    setNonShortlistedProperties(nonShortlisted);
    setIsFilteringComplete(true);
    setLoadingMessage("");
  };

  // Initial load: fetch both properties and shortlisted properties
  useEffect(() => {
    const initializeData = async () => {
      setLoadingMessage("Starting data loading process...");

      // Load shortlisted properties first
      await loadShortlistedProperties();

      // Then load all properties
      await fetchAllProperties();
    };

    initializeData();
  }, []);

  // Filter non-shortlisted properties when both datasets are available and not loading
  useEffect(() => {
    if (!isPropertiesLoading && !isShortlistLoading) {
      if (isPropertiesLoading || isShortlistLoading) {
        // Still loading, update message
        if (isPropertiesLoading) {
          setLoadingMessage("Loading available properties...");
        } else if (isShortlistLoading) {
          setLoadingMessage("Loading customer's shortlisted properties...");
        }
      } else {
        // Both done, start filtering
        filterNonShortlistedProperties();
      }
    }
  }, [
    allProperties,
    shortlistedProperties,
    isPropertiesLoading,
    isShortlistLoading,
  ]);

  // Clear search and filter when navigating via breadcrumbs
  useEffect(() => {
    setSearchTerm("");
    setSelectedOption({
      agency: "All",
      lifestyle: "All",
      type: "All",
      contract_type: "All",
    });
  }, [location.pathname]);

  // Fetch properties when filters/search change
  useEffect(() => {
    if (
      isFilteringComplete
      // &&
      // (searchTerm
      //   ||
      //   selectedOption.type
      //   !== "All"
      //   ||
      //   selectedOption.lifestyle
      //   !== "All" ||
      //   selectedOption.contract_type !== "All"
      // )
    ) {
      setIsFilteringComplete(false);
      fetchAllProperties();
    }
  }, [searchTerm, selectedOption]);

  const handleClearFilters = () => {
    setSelectedOption({
      agency: "All",
      lifestyle: "All",
      type: "All",
      contract_type: "All",
    });
    setIsFilteringComplete(true);
  };

  const handleFilter = () => {
    setCurrentPage(1);
    fetchAllProperties();
  };

  const handleFilterByKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleFilter();
  };

  const handleSearch = (newSearchTerm: string) => {
    const validation = validateAndCleanSearchInput(newSearchTerm);
    setSearchHint(validation.message);
    setSearchTerm(validation.cleanedValue);
    // setSearchTerm(newSearchTerm);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    const selectName = event.target.name;
    setSelectedOption((prevState) => ({
      ...prevState,
      [selectName]: selectedValue,
    }));
    setCurrentPage(1);
  };

  const [filterCount, setFilterCount] = useState(0);
  useEffect(() => {
    const count = Object.values(selectedOption).filter(
      (value) => value !== "All"
    ).length;
    setFilterCount(count);
  }, [selectedOption]);

  // State to store selected properties for shortlisting
  const [selectedProperties, setSelectedProperties] = useState([]);

  const handleCheckboxChange = (propertyId: any, isChecked: any) => {
    if (isChecked) {
      setSelectedProperties((prev) => {
        const newProperty = {
          mp_digital_twin_id: propertyId,
          has_viewed: true,
          has_completed_virtual_tour: true,
          seller_agency_id: sellerAgencyId,
          buyer_agent_id: customerId,
          added_by: "agent",
          is_removed: false,
        };
        return [...prev, newProperty];
      });
    } else {
      setSelectedProperties((prev) =>
        prev.filter((item) => item.mp_digital_twin_id !== propertyId)
      );
    }
  };

  const isPropertySelected = (propertyId: any) => {
    return selectedProperties.some(
      (item) => item.mp_digital_twin_id === propertyId
    );
  };

  const clearSelections = () => {
    setSelectedProperties([]);
  };

  const handleAddPropertyToShortlist = async () => {
    if (selectedProperties.length === 0) {
      alert("Please select at least one property to add to the shortlist.");
      return null;
    }

    try {
      const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/agent/add_property_to_client_property_shortlist/${customerId}`;
      const PAYLOAD = [...selectedProperties];

      const response = await axios.put(API_URL, PAYLOAD, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
      });

      // Refresh shortlisted properties
      await loadShortlistedProperties();

      // Clear selections
      setSelectedProperties([]);

      navigate(`/customer_details?customerId=${customerId}`, {
        state: {
          breadcrumbs: [
            { path: "/", label: "Home" },
            { path: "/customers", label: "My Customers" },
            {
              path: `/customer_details?customerId=${customerId}`,
              label: "Customer Details",
            },
          ],
        },
      });

      return response;
    } catch (error) {
      console.error("Error adding properties to shortlist:", error);
      alert("Failed to add properties to shortlist. Please try again.");

      throw error;
    }
  };

  // Check if user came from customer_details with proper state
  useEffect(() => {
    if (!location.state?.c_email || !location.state?.breadcrumbs) {
      navigate("/customers", { replace: true });
    }
  }, []);

  // Get current page of non-shortlisted properties
  const getCurrentPageProperties = () => {
    const startIndex = (currentPage - 1) * pageLimit;
    const endIndex = startIndex + pageLimit;
    return nonShortlistedProperties.slice(startIndex, endIndex);
  };

  // Calculate total pages for non-shortlisted properties
  const totalPages = Math.max(
    1,
    Math.ceil(nonShortlistedProperties.length / pageLimit)
  );
  const currentPageProperties = getCurrentPageProperties();

  // Check if we're still in the initial loading phase
  const isInitialLoading =
    !isFilteringComplete &&
    (isPropertiesLoading || isShortlistLoading || loadingMessage !== "");
  const isRefiltering =
    !isFilteringComplete &&
    !isPropertiesLoading &&
    !isShortlistLoading &&
    allProperties.length > 0;

  console.log("serach term on Add short list", selectedOption, searchTerm);

  return (
    <>
      <div className="flex-grow">
        <div className="flex flex-start gap-2 px-2">
          <div className="flex flex-1">
            <SearchBar
              onSearch={handleSearch}
              placeholder="Search..."
              value={searchTerm}
              onClick={handleFilter}
              onKeyDown={handleFilterByKey}
            />
          </div>
          <div
            className="bg-[#2E4051] flex items-center justify-center rounded-md w-32 shadow-md cursor-pointer"
            onClick={() => setFilterOpen((pre) => !pre)}
          >
            <Button
              children="Filter"
              className="bg-[#2E4051] rounded-md px-2 h-[40px] text-[0.938rem]"
              onClick={() => console.log("click")}
            />
            {filterCount > 0 && (
              <span className="bg-[#008564] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                {filterCount}
              </span>
            )}
          </div>
        </div>
        {searchHint && (
          <p className="pl-2 mt-2 text-[10px] italic text-red-400">
            {searchHint}
          </p>
        )}

        {filterOpen && (
          <div className="w-full flex px-2 mt-2">
            <div className="w-full flex flex-col flex-start md:items-center gap-1 py-4 shadow-md bg-white dark:bg-[#161F28] rounded-md ">
              <div className="flex relative w-full px-2">
                <span className="flex text-white font-medium text-xl">
                  Filter
                </span>
                <Icon
                  name="ic-close"
                  className="absolute top-1 right-3 w-4 h-4 cursor-pointer"
                  onClick={() => setFilterOpen((pre) => !pre)}
                />
              </div>
              <div className="flex w-full h-[1px] bg-[#2E4051]" />
              <div className="flex flex-col lg:flex-row w-full pt-2 lg:items-center">
                <div className="w-full flex lg:flex-1 flex-wrap flex-col sm:flex-row md:flex-row justify-center">
                  <div className="w-full sm:w-1/2 md:w-1/3 px-2 mb-2 mx-auto">
                    <FilterSelect
                      name="type"
                      label="Type"
                      options={["All", ...Object.keys(type)]}
                      value={selectedOption.type}
                      className="bg-[#2E4051] text-white"
                      handleChange={handleSelectChange}
                    />
                  </div>

                  <div className="w-full sm:w-1/2 md:w-1/3 px-2 mb-2 mx-auto">
                    <FilterSelect
                      name="lifestyle"
                      label="Lifestyle"
                      options={["All", ...Object.keys(lifestyle)]}
                      value={selectedOption.lifestyle}
                      className="bg-[#2E4051] text-white"
                      handleChange={handleSelectChange}
                    />
                  </div>

                  <div className="w-full sm:w-1/2 md:w-1/3 px-2 mb-2 mx-auto">
                    <FilterSelect
                      name="contract_type"
                      label="Contract Type"
                      options={["All", ...Object.keys(contract_type)]}
                      value={selectedOption.contract_type}
                      className="bg-[#2E4051] text-white"
                      handleChange={handleSelectChange}
                    />
                  </div>
                </div>
                <div className="flex flex-row w-full lg:w-52 xl:w-56 gap-4 justify-end px-2 lg:mt-5">
                  <div className="flex">
                    <Button
                      children={`Clear All`}
                      className="bg-[#2E4051] rounded-md text-[12px] sm:text-[11px] md:text-[15px] lg:text-[17px] xl:text-[18px] px-4 h-[40px]"
                      onClick={() => {
                        handleClearFilters();
                        handleFilter();
                        setFilterOpen((pre) => !pre);
                      }}
                    />
                  </div>
                  <div className="flex">
                    <Button
                      children={`Apply`}
                      className="bg-[#008564] rounded-md text-[12px] sm:text-[11px] md:text-[15px] lg:text-[17px] xl:text-[18px] px-4 h-[40px]"
                      onClick={() => {
                        handleFilter();
                        setFilterOpen((pre) => !pre);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isFilteringComplete && selectedProperties.length > 0 && (
          <div className="flex p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex w-full justify-between items-center">
              <div className="flex bg-[#2E4051] items-center justify-center rounded-md max-md:w-full w-68 shadow-md cursor-pointer">
                <Button
                  children={`${selectedProperties.length} Properties Add to Shortlist`}
                  className="flex w-full bg-[#008564] rounded-md items-center justify-center text-[18px] md:px-4 h-[40px] drop-shadow-[0_2px_2px_rgba(0,0,0,0.25)]"
                  onClick={handleAddPropertyToShortlist}
                />
              </div>
              <button
                onClick={clearSelections}
                className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {isInitialLoading || isRefiltering ? (
          <div className="flex flex-col min-h-screen justify-center items-center h-64">
            <CircularProgress />
            <div className="mt-4 text-center">
              <p className="text-[#F4F5F7] dark:text-white font-medium">
                {loadingMessage}
              </p>
              <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {isPropertiesLoading && (
                  <p>Fetching all available properties from your network...</p>
                )}
                {isShortlistLoading && (
                  <p>Loading customer's existing shortlisted properties...</p>
                )}
                {isRefiltering && (
                  <p>Filtering non-shortlisted properties for display...</p>
                )}
                <p className="mt-2 text-xs italic">
                  This may take a moment depending on the number of properties
                </p>
              </div>
            </div>
          </div>
        ) : currentPageProperties.length > 0 ? (
          <>
            <div className="grid gap-3 grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 mt-3 px-2">
              {currentPageProperties.map((property: any) => (
                <div
                  key={property._id}
                  className={`relative grid grid-cols-1 pt-2 pb-2.5 px-2 gap-4 rounded-md bg-white dark:bg-[#0C1117] border border-[#374B5E] cursor-pointer hover:shadow-[0_45px_75px_-18px_rgba(0,0,0,0.5)] hover:-translate-y-1 transition-all duration-400 ${
                    isPropertySelected(property._id)
                      ? "ring-2 ring-[#008564] dark:ring-[#008564]"
                      : ""
                  }`}
                >
                  <div className="property-checkbox absolute bottom-2 right-2 z-10">
                    <input
                      type="checkbox"
                      id={`property-${property._id}`}
                      checked={isPropertySelected(property._id)}
                      onChange={(e) => {
                        handleCheckboxChange(property._id, e.target.checked);
                      }}
                      className="h-5 w-5 text-[#008564] dark:text-[#008564] bg-white dark:bg-gray-800 border-[#008564] dark:border-[#008564] rounded focus:ring-[#008564] dark:focus:ring-[#008564]"
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="relative aspect-video rounded-md overflow-hidden bg-gray-100">
                      <img
                        src={property.property_thumbnail || ""}
                        alt="Property thumbnail"
                        className="object-cover w-full h-full"
                        loading="lazy"
                      />
                      <span
                        className={`flex w-20 absolute right-0 top-0 py-1 px-2 justify-center rounded border-[#008564] text-[11px] max-sm:text-[8px] text-black font-semibold ${
                          property?.property_visibility === "exclusive"
                            ? "border-[#C58300] bg-gradient-to-r from-[#FFC248] via-[#FFE8BB] to-[#FFC248] text-[#664400]"
                            : "border-[linear-gradient(90deg,#209F26,#00D5F6)] bg-[linear-gradient(90deg,#209F26,#00D5F6)] text-[#002904]"
                        } text-shadow-sm`}
                      >
                        {property?.property_visibility === "exclusive"
                          ? "EXCLUSIVE"
                          : "NETWORK"}
                      </span>

                      {isPropertySelected(property._id) && (
                        <div className="absolute inset-0 bg-[#008564] dark:[#008564] opacity-20 pointer-events-none" />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col justify-between gap-2">
                    <PropertyCard
                      digitalTwin={property}
                      isExclusive={false}
                      amount={property?.pricing}
                      contractType={property.characteristics.contract_type}
                      tours={property.characteristics.total_virtual_tours}
                      isCustomerDetails={false}
                      isDetails={false}
                    />
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="m-5">
                <PaginationRounded
                  currentPage={currentPage}
                  totalPages={totalPages}
                  limit={pageLimit}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 text-[#F4F5F7] dark:text-white">
            {nonShortlistedProperties.length === 0 &&
            allProperties.length > 0 ? (
              <>
                <div className="text-lg font-semibold">
                  All available properties are already in the shortlist
                </div>
                <div className="mt-2 text-sm opacity-75">
                  The customer has already shortlisted all{" "}
                  {allProperties.length} available properties
                </div>
              </>
            ) : (
              <>
                <div className="text-lg font-semibold">
                  No properties available
                </div>
                <div className="mt-2 text-sm opacity-75">
                  {searchTerm ||
                  selectedOption.type !== "All" ||
                  selectedOption.lifestyle !== "All" ||
                  selectedOption.contract_type !== "All"
                    ? "Try adjusting your search or filters"
                    : "No properties found in your network"}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {!isUserExist && <AlertDialog />}
    </>
  );
};
